/**
 * Role flags from shared officer login: POST /api/employee-attendance/hr/login
 */

// The employee self-service Attendance portal is postponed. Keep its code and
// routes in place so the UI can be restored later with this single flag.
export const STAFF_ATTENDANCE_UI_ENABLED = false;

const STAFF_ATTENDANCE_PATHS = new Set([
  '/bu-dashboard/dashboard',
  '/bu-dashboard/history',
  '/bu-dashboard/reports',
]);

export function isHrDepartment(department) {
  if (!department || typeof department !== 'string') return false;
  const d = department.trim().toLowerCase();
  return (
    d === 'hr' ||
    d === 'human resources' ||
    d.includes('human resource') ||
    /^hr\b/.test(d) ||
    /\bhr department\b/.test(d)
  );
}

/** Rectorate / Academic Affairs / Registration / Admission (not Administration / Admission) */
export function isAdmissionDepartment(department) {
  if (!department || typeof department !== 'string') return false;
  const d = department.trim().toLowerCase().replace(/\\/g, '/');
  if (d === 'rectorate / academic affairs / registration / admission') return true;
  return (
    d.includes('academic affairs')
    && (d.includes('registration / admission') || d.includes('registration/admission'))
  );
}

/** Rectorate / Academic Affairs / Registration / Examination */
export function isExamDepartment(department) {
  if (!department || typeof department !== 'string') return false;
  const d = department.trim().toLowerCase().replace(/\\/g, '/');
  if (d === 'rectorate / academic affairs / registration / examination') return true;
  if (d.includes('academic affairs') && (d.includes('registration / examination') || d.includes('registration/examination'))) {
    return true;
  }
  const leaf = d.split('/').pop().trim();
  return leaf === 'examination' || leaf === 'exam';
}


/** Rectorate / Academic Affairs / School of Postgraduate Studies and Research Center */
export function isPostgraduateDepartment(department) {
  if (!department || typeof department !== 'string') return false;
  const d = department.trim().toLowerCase().replace(/\\/g, '/');
  return d === 'rectorate / academic affairs / school of postgraduate studies and research center'
    || (d.includes('academic affairs') && d.endsWith('school of postgraduate studies and research center'));
}

/**
 * Parse HR / Admission officer flags from login or /me payload.
 */
export function parseOfficerFlags(result) {
  if (!result) {
    return {
      is_hr: false,
      is_admission: false,
      is_exam: false,
      is_postgraduate: false,
      default_dashboard: null,
      dashboards: {},
      hr_source: null,
      admission_source: null,
      exam_source: null,
      postgraduate_source: null,
    };
  }

  const dashboards = result.dashboards || {};
  const employee = result.employee || {};

  let is_hr = false;
  let hr_source = null;
  if (result.is_hr_officer === true || result.is_hr === true) {
    is_hr = true;
    hr_source = 'is_hr_officer';
  } else if (employee.is_hr_officer === true) {
    is_hr = true;
    hr_source = 'employee.is_hr_officer';
  } else if (dashboards.hr === true) {
    is_hr = true;
    hr_source = 'dashboards.hr';
  } else if (isHrDepartment(employee.department || result.department)) {
    is_hr = true;
    hr_source = 'department';
  }

  let is_admission = false;
  let admission_source = null;
  if (result.is_admission_officer === true) {
    is_admission = true;
    admission_source = 'is_admission_officer';
  } else if (employee.is_admission_officer === true) {
    is_admission = true;
    admission_source = 'employee.is_admission_officer';
  } else if (dashboards.admission === true) {
    is_admission = true;
    admission_source = 'dashboards.admission';
  } else if (isAdmissionDepartment(employee.department || result.department)) {
    is_admission = true;
    admission_source = 'department';
  }

  let is_exam = false;
  let exam_source = null;
  if (result.is_exam_officer === true) {
    is_exam = true;
    exam_source = 'is_exam_officer';
  } else if (employee.is_exam_officer === true) {
    is_exam = true;
    exam_source = 'employee.is_exam_officer';
  } else if (dashboards.exam === true) {
    is_exam = true;
    exam_source = 'dashboards.exam';
  } else if (isExamDepartment(employee.department_complete_name || employee.department || result.department)) {
    is_exam = true;
    exam_source = 'department';
  }

  let is_postgraduate = false;
  let postgraduate_source = null;
  if (result.is_postgraduate_officer === true || employee.is_postgraduate_officer === true) {
    is_postgraduate = true;
    postgraduate_source = 'is_postgraduate_officer';
  } else if (dashboards.postgraduate === true) {
    is_postgraduate = true;
    postgraduate_source = 'dashboards.postgraduate';
  } else if (isPostgraduateDepartment(employee.department_complete_name || employee.department || result.department)) {
    is_postgraduate = true;
    postgraduate_source = 'department';
  }

  let default_dashboard = result.default_dashboard || null;
  if (
    default_dashboard !== 'hr'
    && default_dashboard !== 'admission'
    && default_dashboard !== 'exam'
    && default_dashboard !== 'postgraduate'
  ) {
    if (is_postgraduate && !is_hr && !is_admission && !is_exam) default_dashboard = 'postgraduate';
    else if (is_exam && !is_hr && !is_admission) default_dashboard = 'exam';
    else if (is_admission && !is_hr) default_dashboard = 'admission';
    else if (is_hr) default_dashboard = 'hr';
    else default_dashboard = null;
  }

  return {
    is_hr,
    is_admission,
    is_exam,
    is_postgraduate,
    default_dashboard,
    dashboards,
    hr_source,
    admission_source,
    exam_source,
    postgraduate_source,
  };
}

/** Post-login landing path inside /employee-attendance */
export function getPostLoginPath(user) {
  if (!user) return STAFF_ATTENDANCE_UI_ENABLED ? '/bu-dashboard/dashboard' : '/';
  if (user.default_dashboard === 'postgraduate' || (user.is_postgraduate && !user.is_hr && !user.is_admission && !user.is_exam)) {
    return '/bu-dashboard/postgraduate/students';
  }
  if (user.default_dashboard === 'exam' || (user.is_exam && !user.is_hr && !user.is_admission)) {
    return '/bu-dashboard/exams/entries';
  }
  if (user.default_dashboard === 'admission' || (user.is_admission && !user.is_hr)) {
    return '/bu-dashboard/admissions/students';
  }
  if (user.default_dashboard === 'hr' || user.is_hr) {
    return '/bu-dashboard/hr/attendance';
  }
  if (user.is_tutor) return '/attendance/tutor/dashboard';
  if (user.is_faculty_admin) return '/attendance/faculty-admin/dashboard';
  if (user.is_camera_media || user.roles?.includes('camera_media')) return '/attendance/camera/calendar';
  if (user.is_utilities || user.roles?.includes('utilities')) return '/bu-dashboard/utilities/dashboard';
  return STAFF_ATTENDANCE_UI_ENABLED ? '/bu-dashboard/dashboard' : '/';
}

/** Prevent an API-provided landing path from reopening the postponed portal. */
export function getVisiblePostLoginPath(user, requestedPath) {
  if (
    requestedPath
    && (STAFF_ATTENDANCE_UI_ENABLED || !STAFF_ATTENDANCE_PATHS.has(requestedPath))
  ) {
    return requestedPath;
  }
  return getPostLoginPath(user);
}

/** @deprecated use parseOfficerFlags — kept for existing imports */
export function getHrFlagsFromStaffResponse(result) {
  const flags = parseOfficerFlags(result);
  return { is_hr: flags.is_hr, hr_source: flags.hr_source };
}

export function mergeHrAccess(staffFlags, apiMeta) {
  const is_hr = Boolean(staffFlags?.is_hr || apiMeta?.is_hr);
  const is_admission = Boolean(staffFlags?.is_admission || apiMeta?.is_admission);
  const is_exam = Boolean(staffFlags?.is_exam || apiMeta?.is_exam);
  const is_postgraduate = Boolean(staffFlags?.is_postgraduate || apiMeta?.is_postgraduate);
  const default_dashboard =
    apiMeta?.default_dashboard ||
    staffFlags?.default_dashboard ||
    (is_postgraduate && !is_hr && !is_admission && !is_exam
      ? 'postgraduate'
      : is_exam && !is_hr && !is_admission
      ? 'exam'
      : is_admission && !is_hr
        ? 'admission'
        : is_hr
          ? 'hr'
          : null);

  return {
    ...apiMeta,
    is_hr,
    is_admission,
    is_exam,
    is_postgraduate,
    default_dashboard,
    dashboards: apiMeta?.dashboards || staffFlags?.dashboards || {},
    hr_source: apiMeta?.is_hr ? apiMeta.hr_source || 'hr_api' : staffFlags?.hr_source,
    admission_source: apiMeta?.is_admission
      ? apiMeta.admission_source || 'hr_api'
      : staffFlags?.admission_source,
    exam_source: apiMeta?.is_exam
      ? apiMeta.exam_source || 'hr_api'
      : staffFlags?.exam_source,
    hr_api_missing: Boolean(apiMeta?.hr_api_missing && is_hr && !apiMeta?.is_hr),
    hr_token_ready: Boolean(apiMeta?.hr_token_ready),
    hr_api_error: apiMeta?.hr_api_error || null,
  };
}
